// Compact filter popout used in the view-bar above the workstreams.
// Wraps the existing Owner / Status selectors so the page header stays
// quiet — the filters only appear when someone actually wants to narrow
// the task list down. Matches the design's "Filter" button-to-popout
// pattern from the project detail.

import { useEffect, useRef, useState } from 'react';

import { displayName, STATUS_LABELS, STATUS_ORDER } from '../../pm/constants';
import { AppUser, TaskStatus } from '../../pm/types';

interface Props {
  admins: AppUser[];
  ownerFilter: string; // uid, 'all', or 'unassigned'
  statusFilter: TaskStatus | 'all';
  onOwnerChange: (v: string) => void;
  onStatusChange: (v: TaskStatus | 'all') => void;
}

const ProjectFilterMenu: React.FC<Props> = ({
  admins: users,
  ownerFilter,
  statusFilter,
  onOwnerChange,
  onStatusChange,
}) => {
  const admins = users.filter((u) => u.isOperator);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Close on click-outside. Mousedown rather than click so a select-option
  // click inside the menu doesn't race with the dismiss.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const activeCount =
    (ownerFilter !== 'all' ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0);

  return (
    <div className="pm-task-filter" ref={wrapRef}>
      <button
        type="button"
        className={`pm-button pm-button-ghost pm-button-sm${activeCount > 0 ? ' is-on' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        Filter
        {activeCount > 0 ? (
          <span className="pm-task-filter-count">{activeCount}</span>
        ) : null}
      </button>

      {open ? (
        <div className="pm-task-filter-menu" role="dialog" aria-label="Task filters">
          <label className="pm-task-filter-row">
            <span className="pm-task-filter-label">Owner</span>
            <select
              value={ownerFilter}
              onChange={(e) => onOwnerChange(e.target.value)}
            >
              <option value="all">All</option>
              <option value="unassigned">Unassigned</option>
              {admins.map((a) => (
                <option key={a.uid} value={a.uid}>
                  {displayName(a.email)}
                </option>
              ))}
            </select>
          </label>

          <label className="pm-task-filter-row">
            <span className="pm-task-filter-label">Status</span>
            <select
              value={statusFilter}
              onChange={(e) => onStatusChange(e.target.value as TaskStatus | 'all')}
            >
              <option value="all">All</option>
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>

          {activeCount > 0 ? (
            <button
              type="button"
              className="pm-link-button pm-task-filter-clear"
              onClick={() => {
                onOwnerChange('all');
                onStatusChange('all');
                setOpen(false);
              }}
            >
              Clear filters
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ProjectFilterMenu;
